import { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Camera, X, Copy, Sparkles, Send, Trash2, Loader2, MailCheck } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";
import { KYCBadge } from "./director/KYCReviewPanel";
import { RailSelector, PhonePeBadge, RailChip } from "./payments/RailSelector";

/* ─── Founder Photo Upload ─────────────────────────────────── */
export function FounderPhotoUploader({ api, director, onDone }) {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const ref = useRef(null);
  const url = director?.photo_key && director?.id
    ? `${process.env.REACT_APP_BACKEND_URL}/api/founders/photo/${director.id}?v=${Date.now()}`
    : null;

  const upload = async (f) => {
    if (!f) return;
    setUploading(true);
    setPreview(URL.createObjectURL(f));
    try {
      const fd = new FormData(); fd.append("file", f);
      await api.post("/director/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Portrait updated · shown across the Suite.");
      onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed."); setPreview(null); }
    finally { setUploading(false); }
  };

  return (
    <div className="flex items-center gap-4" data-testid="director-photo-uploader">
      <div className="relative w-20 h-20 rounded-full border-2 border-[#C9A961]/50 grid place-items-center bg-[#0a0806] overflow-hidden shrink-0"
           style={{ boxShadow: "0 0 20px rgba(201,169,97,0.2)" }}>
        {preview || url ? (
          <img src={preview || url} alt="Founder portrait" className="w-full h-full object-cover" />
        ) : (
          <span className="font-serif text-2xl text-[#C9A961]">
            {(director?.name || "V").split(" ").map((s) => s[0]).slice(0, 2).join("")}
          </span>
        )}
      </div>
      <div>
        <div className="font-grotesk text-[9px] uppercase tracking-[0.28em] text-[#C9A961]">Founder Portrait</div>
        <div className="mt-1 flex items-center gap-2">
          <button onClick={() => ref.current?.click()}
            data-testid="director-photo-btn"
            className="inline-flex items-center gap-2 rounded-sm border border-[#C9A961]/50 text-[#C9A961] px-3 py-1.5 font-grotesk text-[10px] uppercase tracking-[0.24em] hover:bg-[#C9A961] hover:text-black transition-colors">
            <Camera size={12} /> {uploading ? "Uploading…" : (url ? "Replace" : "Upload")}
          </button>
          <input hidden ref={ref} type="file" accept="image/*"
            onChange={(e) => upload(e.target.files?.[0])} />
          <span className="font-body text-[10px] text-[#8A8A82]">JPG/PNG · &lt; 5MB</span>
        </div>
      </div>
    </div>
  );
}

/* ─── Concierge Unread Dot ─────────────────────────────────── */
export function ConciergeDot({ api }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get("/director/concierge/unread-count");
        if (alive) setN(data.count || 0);
      } catch { /* silent */ }
    };
    load();
    const t = setInterval(load, 20000);
    return () => { alive = false; clearInterval(t); };
  }, [api]);
  if (n === 0) return null;
  return (
    <div className="fixed top-6 right-6 z-40 flex items-center gap-2 rounded-full border border-[#C9A961] bg-[#0a0806]/95 backdrop-blur px-3 py-2 shadow-[0_0_25px_rgba(201,169,97,0.35)]"
         data-testid="director-concierge-dot">
      <motion.span animate={{ scale: [1, 1.4, 1], opacity: [0.7, 1, 0.7] }}
        transition={{ duration: 1.5, repeat: Infinity }}
        className="w-2.5 h-2.5 rounded-full bg-[#C9A961]" />
      <span className="font-grotesk text-[9px] uppercase tracking-[0.28em] text-[#C9A961]">
        {n} new Board note{n > 1 ? "s" : ""}
      </span>
    </div>
  );
}

/* ─── Admin · Invite Manager ───────────────────────────────── */
export const FEE_SLABS = [
  { key: "full", label: "Full Fee", amount: 250000, note: "Standard authorization fee. No waiver applied." },
  { key: "reduced", label: "Reduced Fee", amount: 125000, note: "50% concession. Applicable for select founder profiles." },
  { key: "compliance", label: "Compliance Filing", amount: 85000, note: "Fee waived. Compliance filing charges of ₹85,000 are applicable." },
  { key: "free", label: "Free Filing", amount: 0, note: "Free filing applicable. Processing subject to a waiting window of 25 business days." },
];
export function AdminDirectorInvites({ api }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", email: "", country: "IN", preset_shareholding_pct: 50, fee_slab: "full", kyc_mode: "A", note: "", payment_rail: "razorpay" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setItems((await api.get("/admin/directors/invites")).data); }
    catch { /* silent */ }
    finally { setLoading(false); }
  }, [api]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { window.addEventListener("vinci:kyc-changed", load); return () => window.removeEventListener("vinci:kyc-changed", load); }, [load]);

  const create = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post("/admin/directors/invites", form);
      if (data.delivered) toast.success("Invite issued · email sent to Founder.");
      else toast.warning("Invite issued — e-mail NOT delivered", { description: data.delivery_hint || "Delivery could not be confirmed.", duration: 12000 });
      setForm({ name: "", email: "", country: "IN", preset_shareholding_pct: 50, fee_slab: "full", kyc_mode: "A", note: "", payment_rail: "razorpay" });
      load();
      try { await navigator.clipboard.writeText(data.invite_url); toast.success("Link copied."); }
      catch { /* clipboard denied — ignore */ }
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create invite."); }
    finally { setBusy(false); }
  };

  const copy = async (link) => {
    try { await navigator.clipboard.writeText(link); toast.success("Copied to clipboard."); }
    catch { toast.error("Could not copy."); }
  };

  const [showRevoked, setShowRevoked] = useState(false);
  const [pendingRevoke, setPendingRevoke] = useState(null);
  const [revoking, setRevoking] = useState(false);
  const revoke = async () => {
    if (!pendingRevoke) return;
    setRevoking(true);
    try {
      await api.delete(`/admin/directors/invites/${pendingRevoke.token}`);
      toast.success("Invitation revoked", { description: `${pendingRevoke.name} · link permanently disabled` });
      setPendingRevoke(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not revoke invitation."); }
    finally { setRevoking(false); }
  };
  const [resending, setResending] = useState(null);
  const [resentNote, setResentNote] = useState({});
  const resend = async (i) => {
    setResending(i.token);
    try {
      const { data } = await api.post(`/admin/directors/invites/${i.token}/resend`);
      if (data.delivered) {
        setResentNote((n) => ({ ...n, [i.token]: `Invitation resent to ${data.email}` }));
        setTimeout(() => setResentNote((n) => { const c = { ...n }; delete c[i.token]; return c; }), 6000);
      } else {
        toast.warning("Resend attempted — e-mail NOT delivered", { description: data.delivery_hint || "Delivery could not be confirmed.", duration: 12000 });
      }
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not resend invitation."); }
    finally { setResending(null); }
  };
  const revokedCount = items.filter((i) => i.revoked_at).length;
  const visible = showRevoked ? items : items.filter((i) => !i.revoked_at);

  return (
    <div className="rounded-sm border border-[#C9A961]/25 bg-[#0f0d0a] p-6 mb-8" data-testid="admin-director-invites">
      <div className="flex items-center gap-2 mb-4">
        <Sparkles size={16} className="text-[#C9A961]" />
        <h3 className="font-serif text-xl text-[#F5F5F0]">Founder Invitations · Special Onboarding Links</h3>
      </div>
      <p className="font-body text-sm text-[#C9C9C2] mb-6 leading-relaxed">
        Issue a private link that locks in the Founder's cap-table stake and
        the Authorization Fee Structure at issuance. Perfect for elite Founders like a
        50% co-owner from overseas.
      </p>
      <form onSubmit={create} className="grid md:grid-cols-2 gap-4 mb-6">
        <input required placeholder="Founder full name" value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          data-testid="invite-name" className="bg-transparent border-b border-white/15 focus:border-[#C9A961] outline-none py-2 font-body text-[#F5F5F0]" />
        <input required type="email" placeholder="Founder email" value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          data-testid="invite-email" className="bg-transparent border-b border-white/15 focus:border-[#C9A961] outline-none py-2 font-body text-[#F5F5F0]" />
        <select value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })}
          data-testid="invite-country" className="bg-[#0a0806] border border-white/15 focus:border-[#C9A961] outline-none py-2 px-3 font-body text-[#F5F5F0]">
          <option value="IN">🇮🇳 India</option>
          <option value="US">🇺🇸 United States</option>
          <option value="GB">🇬🇧 United Kingdom</option>
          <option value="AE">🇦🇪 UAE</option>
          <option value="SG">🇸🇬 Singapore</option>
        </select>
        <div>
          <input required type="number" min={0} max={100} step="0.01" placeholder="Shareholding %" value={form.preset_shareholding_pct}
            onChange={(e) => setForm({ ...form, preset_shareholding_pct: parseFloat(e.target.value || "0") })}
            data-testid="invite-share" className="bg-transparent border-b border-white/15 focus:border-[#C9A961] outline-none py-2 font-body text-[#F5F5F0] w-full" />
          <span className="font-body text-[10px] text-[#8A8A82]">Locked at authorization time</span>
        </div>
        <fieldset className="col-span-full" data-testid="invite-fee-slabs">
          <legend className="font-grotesk text-[9px] uppercase tracking-[0.28em] text-[#C9A961] mb-3">Authorization Fee Structure · locked into the link at issuance</legend>
          <div className="grid md:grid-cols-2 gap-2">
            {FEE_SLABS.map((sl) => {
              const on = form.fee_slab === sl.key;
              return (
                <label key={sl.key} data-testid={`invite-fee-slab-${sl.key}`}
                  className={`cursor-pointer rounded-sm border p-3.5 transition-colors ${on ? "border-[#C9A961] bg-[#C9A961]/10" : "border-white/10 bg-black/30 hover:border-[#C9A961]/40"}`}>
                  <div className="flex items-start gap-3">
                    <input type="radio" name="fee_slab" value={sl.key} checked={on} onChange={() => setForm({ ...form, fee_slab: sl.key })} className="mt-1 accent-[#C9A961]" />
                    <div className="min-w-0">
                      <div className="flex items-baseline justify-between gap-3">
                        <span className="font-serif text-[#F5F5F0]">{sl.label}</span>
                        <span className={`font-grotesk text-[10px] tracking-[0.18em] ${on ? "text-[#C9A961]" : "text-[#C9C9C2]"}`}>INR {sl.amount.toLocaleString("en-IN")}{sl.amount ? ` + GST = ${(Math.round(sl.amount * 1.18)).toLocaleString("en-IN")}` : ""}</span>
                      </div>
                      <div className="font-body text-[11px] text-[#8A8A82] mt-1 leading-relaxed">{sl.note}</div>
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
        </fieldset>
        {["US", "GB"].includes((form.country || "").toUpperCase()) && (
          <fieldset className="col-span-full" data-testid="invite-kyc-modes">
            <legend className="font-grotesk text-[9px] uppercase tracking-[0.28em] text-[#C9A961] mb-3">Foreign Partner eKYC · trigger mode (USA / UK)</legend>
            <div className="grid md:grid-cols-2 gap-2">
              {[["A", "Mode A · KYC before activation", "Invitation link activates only once eKYC is approved. Partner cannot reach the fee step until verified."],
                ["B", "Mode B · Parallel KYC", "Link activates immediately; eKYC and fee run in parallel. Full account activation is held until eKYC is approved."]].map(([k, l, d]) => {
                const on = form.kyc_mode === k;
                return (
                  <label key={k} data-testid={`invite-kyc-mode-${k}`} className={`cursor-pointer rounded-sm border p-3.5 transition-colors ${on ? "border-[#C9A961] bg-[#C9A961]/10" : "border-white/10 bg-black/30 hover:border-[#C9A961]/40"}`}>
                    <div className="flex items-start gap-3">
                      <input type="radio" name="kyc_mode" value={k} checked={on} onChange={() => setForm({ ...form, kyc_mode: k })} className="mt-1 accent-[#C9A961]" />
                      <div><div className="font-serif text-[#F5F5F0]">{l}</div><div className="font-body text-[11px] text-[#8A8A82] mt-1 leading-relaxed">{d}</div></div>
                    </div>
                  </label>);
              })}
            </div>
          </fieldset>)}
        <div className="col-span-full">
          <RailSelector testId="invite-rail" value={form.payment_rail} onChange={(r) => setForm({ ...form, payment_rail: r })}
                        label="Fee Payment Rail · both links are generated, this one is emailed" />
        </div>
        <textarea placeholder="Personal note (optional, appears in invite email + Chairman's Letter)"
          value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
          data-testid="invite-note" rows={2}
          className="col-span-full bg-transparent border border-white/15 focus:border-[#C9A961] rounded-sm p-3 font-body text-sm text-[#F5F5F0] outline-none" />
        <button type="submit" disabled={busy} data-testid="invite-submit"
          className="col-span-full justify-self-start rounded-sm bg-[#C9A961] text-black px-6 py-2.5 font-grotesk text-[10px] uppercase tracking-[0.28em] hover:bg-[#8B7333] hover:text-[#F5F5F0] transition-colors disabled:opacity-60 inline-flex items-center gap-2">
          <Send size={13} /> {busy ? "Issuing…" : "Issue invite & email link"}
        </button>
      </form>

      <div className="flex items-center justify-between gap-3 mb-3">
        <span className="font-grotesk text-[9px] uppercase tracking-[0.26em] text-[#8A8A82]">{visible.length} link{visible.length === 1 ? "" : "s"}{revokedCount ? ` · ${revokedCount} revoked` : ""}</span>
        <label className="inline-flex items-center gap-2 cursor-pointer select-none" data-testid="invite-show-revoked">
          <input type="checkbox" checked={showRevoked} onChange={(e) => setShowRevoked(e.target.checked)} className="accent-[#C9A961]" />
          <span className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#C9C9C2]">Show revoked</span>
        </label>
      </div>
      {loading ? <p className="font-body text-[#8A8A82]">Loading…</p>
        : visible.length === 0 ? <p className="font-body text-[#8A8A82] italic">{items.length ? "No active invites — revoked entries are hidden." : "No invites issued yet."}</p>
        : <div className="space-y-2">
            {visible.map((i) => {
              const revoked = !!i.revoked_at;
              return (
              <div key={i.token} data-testid={`invite-row-${i.token.slice(0,8)}`}
                className={`rounded-sm border p-4 flex flex-wrap items-center gap-4 ${revoked ? "border-white/5 bg-black/20 opacity-60" : "border-white/10 bg-black/40"}`}>
                <div className="min-w-0 flex-1">
                  <div className={`font-serif ${revoked ? "text-[#8A8A82] line-through decoration-white/20" : "text-[#F5F5F0]"}`}>
                    {i.name} · <span className={revoked ? "text-[#8A8A82]" : "text-[#C9A961]"}>{i.preset_shareholding_pct.toFixed(2)}%</span>
                    {!revoked && <span data-testid={`invite-fee-display-${i.token.slice(0,8)}`} className="ml-2 font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#C9A961]">· {i.fee_display}</span>}
                    {!revoked && i.kyc?.applies && <span className="ml-2"><KYCBadge kyc={i.kyc} testid={`invite-kyc-badge-${i.token.slice(0,8)}`} /></span>}
                    {i.used_at && <span className="ml-2 font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A8A82]">USED · {new Date(i.used_at).toLocaleDateString()}</span>}
                    {revoked && <span data-testid={`invite-revoked-badge-${i.token.slice(0,8)}`} className="ml-2 inline-block border border-white/15 px-1.5 py-0.5 font-grotesk text-[8px] uppercase tracking-[0.22em] text-[#8A8A82] no-underline">REVOKED · {new Date(i.revoked_at).toLocaleDateString()}</span>}
                  </div>
                  <div className="font-body text-xs text-[#8A8A82] truncate">{i.email} · {i.invite_url}{revoked && i.revoked_by ? ` · revoked by ${i.revoked_by}` : ""}</div>
                  {!revoked && i.last_delivery && !i.last_delivery.delivered && (
                    <div data-testid={`invite-delivery-warning-${i.token.slice(0,8)}`} title={i.last_delivery.hint || ""} className="mt-1.5 inline-flex items-center gap-1.5 font-grotesk text-[9px] uppercase tracking-[0.2em] text-[#E5484D]/80">
                      <X size={11} /> Last e-mail not delivered · {i.last_delivery.hint || "see Admin Notifications"}
                    </div>
                  )}
                  {resentNote[i.token] && (
                    <div data-testid={`invite-resent-note-${i.token.slice(0,8)}`} className="mt-1.5 inline-flex items-center gap-1.5 font-grotesk text-[9px] uppercase tracking-[0.2em] text-[#C9A961]/80">
                      <MailCheck size={11} /> {resentNote[i.token]}
                    </div>
                  )}
                </div>
                {revoked ? (
                  <span data-testid={`invite-deleted-${i.token.slice(0,8)}`} className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A8A82] px-3 py-1.5 border border-white/10 rounded-sm">Deleted</span>
                ) : (
                  <div className="flex items-center gap-2 flex-wrap justify-end">
                    <button onClick={() => copy(i.invite_url)} data-testid={`invite-copy-${i.token.slice(0,8)}`}
                      className="inline-flex items-center gap-2 rounded-sm border border-[#C9A961]/40 text-[#C9A961] px-3 py-1.5 font-grotesk text-[9px] uppercase tracking-[0.22em] hover:bg-[#C9A961] hover:text-black transition-colors">
                      <Copy size={11} /> Copy link
                    </button>
                    {i.pay_links?.razorpay?.url && !i.fee_paid_at && (
                      <button onClick={() => copy(i.pay_links.razorpay.url)} data-testid={`invite-copy-razorpay-${i.token.slice(0,8)}`}
                        title="Razorpay fee link"
                        className={`inline-flex items-center gap-2 rounded-sm border px-3 py-1.5 font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#C9C9C2] hover:border-[#4285F4] hover:text-[#4285F4] transition-colors ${(i.payment_rail || "razorpay") === "razorpay" ? "border-[#C9A961]/60" : "border-white/15"}`}>
                        <Copy size={11} /> Razorpay fee link
                      </button>
                    )}
                    {i.pay_links?.phonepe?.url && !i.fee_paid_at && (
                      <button onClick={() => copy(i.pay_links.phonepe.url)} data-testid={`invite-copy-phonepe-${i.token.slice(0,8)}`}
                        title="PhonePe fee link"
                        className={`inline-flex items-center gap-2 rounded-sm border px-3 py-1.5 font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#C9C9C2] hover:border-[#5F259F] hover:text-[#C9A6F0] transition-colors ${i.payment_rail === "phonepe" ? "border-[#C9A961]/60" : "border-white/15"}`}>
                        <PhonePeBadge size={12} /> <Copy size={11} /> PhonePe fee link
                      </button>
                    )}
                    {i.pay_links && Object.keys(i.pay_links).length > 0 && !i.fee_paid_at && (
                      <select value={i.payment_rail || "razorpay"} data-testid={`invite-rail-${i.token.slice(0,8)}`}
                        onChange={async (e) => { const nextRail = e.target.value; try { await api.post(`/admin/directors/invites/${i.token}/rail`, { rail: nextRail }); toast.success(`Resend will use ${nextRail === "phonepe" ? "PhonePe" : "Razorpay"}`); load(); } catch (er) { toast.error(er?.response?.data?.detail || "Could not switch rail"); } }}
                        className="bg-black/40 border border-white/15 rounded-sm px-2 py-1.5 font-grotesk text-[9px] uppercase tracking-[0.18em] text-[#C9C9C2]">
                        <option value="razorpay">Send: Razorpay</option>
                        <option value="phonepe">Send: PhonePe</option>
                      </select>
                    )}
                    {i.fee_paid_at && <RailChip rail={i.fee_provider || "razorpay"} />}
                    {!i.used_at && (() => {
                      const maxed = (i.resend_count || 0) >= (i.resend_limit || 3);
                      return (
                        <button onClick={() => !maxed && resend(i)} disabled={maxed || resending === i.token}
                          data-testid={`invite-resend-${i.token.slice(0,8)}`}
                          title={maxed ? "Resend limit reached. Please revoke and issue a new invitation." : `Resend invitation e-mail (${i.resend_count || 0}/${i.resend_limit || 3} used)`}
                          className={`inline-flex items-center gap-2 rounded-sm border px-3 py-1.5 font-grotesk text-[9px] uppercase tracking-[0.22em] transition-colors ${maxed
                            ? "border-white/10 text-[#8A8A82]/60 cursor-not-allowed"
                            : "border-[#D8B23E]/40 text-[#D8B23E] hover:bg-[#D8B23E]/15 hover:border-[#D8B23E]/70"}`}>
                          {resending === i.token ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />}
                          Resend{(i.resend_count || 0) > 0 ? ` · ${i.resend_count}/${i.resend_limit || 3}` : ""}
                        </button>
                      );
                    })()}
                    {!i.used_at && (
                      <button onClick={() => setPendingRevoke(i)} data-testid={`invite-revoke-${i.token.slice(0,8)}`} title="Revoke invitation"
                        className="inline-flex items-center justify-center rounded-sm border border-[#E5484D]/30 text-[#E5484D]/80 h-8 w-8 hover:bg-[#E5484D]/15 hover:text-[#E5484D] hover:border-[#E5484D]/60 transition-colors">
                        <Trash2 size={13} />
                      </button>
                    )}
                  </div>
                )}
              </div>);
            })}
          </div>
      }
      <AlertDialog open={!!pendingRevoke} onOpenChange={(o) => !o && !revoking && setPendingRevoke(null)}>
        <AlertDialogContent data-testid="invite-revoke-dialog" className="bg-[#0a0a0a] border border-[#E5484D]/40 text-[#F5F5F0]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl">Revoke this invitation?</AlertDialogTitle>
            <AlertDialogDescription className="font-body text-sm text-[#C9C9C2] leading-relaxed">
              Are you sure you want to revoke this invitation? The link will be permanently disabled and the founder will no longer be able to use it.
              {pendingRevoke && <span className="block mt-3 font-grotesk text-[10px] uppercase tracking-[0.2em] text-[#8A8A82]">{pendingRevoke.name} · {pendingRevoke.email}</span>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="invite-revoke-cancel" disabled={revoking} className="bg-transparent border-white/20 text-[#C9C9C2] hover:bg-white/5 hover:text-[#F5F5F0] font-grotesk text-[10px] uppercase tracking-[0.22em]">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="invite-revoke-confirm" disabled={revoking} onClick={(e) => { e.preventDefault(); revoke(); }}
              className="bg-[#B4262A] text-[#F5F5F0] hover:bg-[#E5484D] font-grotesk text-[10px] uppercase tracking-[0.22em]">
              {revoking ? <Loader2 size={12} className="animate-spin" /> : "Revoke Permanently"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
